// Types matching reporting-api backend responses.
//
// These live in @frontend/core (not the reporting app) because they're shared
// across the monorepo. Import from './types' not the barrel './index' to avoid
// pulling in the RTK Query api module — important for test files that shouldn't
// depend on the Redux store.

// Sports supported by the reporting-api backend today. Adding a sport
// here means adding a corresponding stats interface below and a config
// in the reporting app's sport-config.ts.
//
// `ReportingSport` is a const-object enum rather than a bare string
// union so callers get auto-complete + a single canonical source of
// truth (`ReportingSport.Soccer`, `ReportingSport.AmericanFootball`).
// The underlying runtime values stay as the snake-case strings the
// backend emits, so existing string comparisons keep working.
export const ReportingSport = {
  Soccer: 'soccer',
  AmericanFootball: 'american_football',
  CanadianFootball: 'canadian_football'
} as const

export type ReportingSport =
  (typeof ReportingSport)[keyof typeof ReportingSport]

// American Football + Canadian Football share most of their pitch /
// stat / visualisation code — use `isGridironSport` whenever the two
// need to be treated as a single group. "Gridiron" is the neutral
// umbrella term; avoid the word "football" for this because in most of
// the world football means soccer.
export function isGridironSport(sport: ReportingSport): boolean {
  return (
    sport === ReportingSport.AmericanFootball ||
    sport === ReportingSport.CanadianFootball
  )
}

// The API returns sport as various strings ('American Football',
// 'american_football', 'CanadianFootball', etc.). This normalises them
// to `ReportingSport`. CFL resolves to its own value so the player-view
// can draw the correct pitch; non-pitch config fields for CFL mirror
// AMF where they overlap. Falls back to soccer since most deployments
// are soccer-only.
export function normaliseSportType(
  sport: string | null | undefined
): ReportingSport {
  if (!sport) return ReportingSport.Soccer
  const lower = sport.toLowerCase().replace(/[\s-]/g, '_')
  if (lower === 'canadian_football' || lower === 'canadianfootball') {
    return ReportingSport.CanadianFootball
  }
  if (lower === 'american_football' || lower === 'americanfootball') {
    return ReportingSport.AmericanFootball
  }
  return ReportingSport.Soccer
}

// GET /teams response item
export interface ReportingTeam {
  id: string
  name: string
  sport?: string
  colour?: string
  logo?: string
}

// GET /teams/{teamId}/players response item
export interface ReportingPlayer {
  id: string
  firstName: string
  lastName: string
  displayName: string
  number: number | null
  position: string | null
  image: string
}

// Drill within a session
export interface ReportingDrill {
  id: string
  name: string
  // Optional polygon geometry keyed by corner id. P1..P4 are the rectangle
  // corners (required for the drill-zone overlay to render); P5..P8 are
  // optional goalpost anchors used for rugby / American Football. When the
  // backend doesn't surface this field the player-view pitch simply skips
  // the overlay — it's purely additive.
  region?: DrillRegion
}

export interface DrillRegionPoint {
  x: number
  y: number
  z: number
}
export type DrillRegion = Record<string, DrillRegionPoint>

// Period within a session
export interface ReportingPeriod {
  id: string
  periodNumber: number | null
  overtime: boolean
}

// Session pitch geometry returned by the reporting-api. Shape mirrors the
// upstream pitch row — `pitch` is the P1..Pn coordinate map the pitch
// rendering uses directly. Absent when the session has no pitch assigned,
// in which case the reporting app falls back to the per-sport default.
export interface ReportingSessionPitch {
  name: string
  sport: string
  pitch: Record<string, { x: number; y: number; z: number }>
  anchors?: Record<string, unknown>
  objects?: Record<string, { x: number; y: number; z: number }>
}

// GET /teams/{teamId}/sessions response item
export interface ReportingSession {
  id: string
  name: string
  date: string
  type: string
  // Raw backend value (e.g. "Soccer", "AmericanFootball"). Normalise via
  // normaliseSportType() before using as a config key.
  sport: string
  pitch?: ReportingSessionPitch
  // Backend may omit these arrays (null) for sessions with no drills /
  // periods. Always guard before calling `.find` / `.map`.
  drills: ReportingDrill[] | null
  periods: ReportingPeriod[] | null
}

// The API wraps most stats in this shape to provide comparison context.
// Some endpoints return plain numbers instead (inconsistently), which is why
// getStatValue() below must handle both. The seasonAvg/maxValue/minValue
// fields drive the performance bar UI (green bar + white dot marker).
export interface StatValue {
  value: number | null
  seasonAvg: number
  maxValue: number
  minValue: number
}

// The API inconsistently returns StatValue objects or plain numbers depending
// on the endpoint and whether comparison data exists. This normalises both
// to a simple number for display. Used everywhere stats are accessed.
export function getStatValue(
  stat: StatValue | number | null | undefined
): number | null {
  if (stat == null) return null
  if (typeof stat === 'number') return stat
  if (typeof stat === 'object' && 'value' in stat) {
    return stat.value
  }
  return null
}

// Helper to extract full StatValue object (for comparison bars)
export function getFullStatValue(
  stat: StatValue | number | null | undefined
): StatValue | null {
  if (stat == null) return null
  if (typeof stat === 'object' && 'value' in stat) return stat
  return null
}

// Soccer player stats — every field the reporting-api can return for a soccer player.
// Fields are optional because the API omits stats the player doesn't have (e.g., a
// defender won't have shot stats). Every field can be either a StatValue (with
// season comparisons) or a plain number, hence the union on each field.
export interface SoccerPlayerSummaryStats {
  totalDistance_m?: StatValue | number
  distancePerMinute_m?: StatValue | number
  totalDuration_s?: StatValue | number
  maxSpeed_mps?: StatValue | number
  totalMechanicalWork_kJ?: StatValue | number

  sprintCount?: StatValue | number
  sprintDistance_m?: StatValue | number
  sprintDuration_s?: StatValue | number
  highSpeedRunCount?: StatValue | number
  highSpeedRunDistance_m?: StatValue | number
  highSpeedRunDuration_s?: StatValue | number

  relativeSprint80Count?: StatValue | number
  relativeSprint80Distance_m?: StatValue | number
  relativeSprint80Duration_s?: StatValue | number

  highMetabolicPowerCount?: StatValue | number
  highMetabolicPowerDistance_m?: StatValue | number
  highMetabolicPowerDuration_s?: StatValue | number

  highIntensityAccelCount?: StatValue | number
  highIntensityAccelDistance_m?: StatValue | number
  highIntensityAccelDuration_s?: StatValue | number
  highIntensityDecelCount?: StatValue | number
  highIntensityDecelDistance_m?: StatValue | number
  highIntensityDecelDuration_s?: StatValue | number

  passesAttempted?: StatValue | number
  passesAttemptedP90?: StatValue | number
  passesMade?: StatValue | number
  forwardPasses?: StatValue | number
  sidePasses?: StatValue | number
  backPasses?: StatValue | number
  passCompletionPct?: StatValue | number
  forwardPassCompletionPct?: StatValue | number
  sidePassCompletionPct?: StatValue | number
  backPassCompletionPct?: StatValue | number
  passSpeedAvg_mps?: StatValue | number
  passTimeOnBallAvg_s?: StatValue | number

  shotsAttempted?: StatValue | number
  shotsAttemptedP90?: StatValue | number
  shotsOnTarget?: StatValue | number
  shotsOnTargetPct?: StatValue | number
  goals?: StatValue | number
  shotSpeedAvg_mps?: StatValue | number
  shotTimeOnBallAvg_s?: StatValue | number
  conversionPct?: StatValue | number

  carries?: StatValue | number
  carrySpeedAvg_mps?: StatValue | number
  carryDistAvg_m?: StatValue | number
  carriesP90?: StatValue | number

  involvements?: StatValue | number
  involvementsPerMinute?: StatValue | number
  involvementsP90?: StatValue | number

  // Duels & defensive actions
  duelCount?: StatValue | number
  duelWonPct?: StatValue | number
  tackleCount?: StatValue | number
  tacklesWonPct?: StatValue | number
  clearances?: StatValue | number
  blocks?: StatValue | number
  interceptions?: StatValue | number
  defensiveActionCount?: StatValue | number
  defensiveActionCountP90?: StatValue | number

  // GK actions
  gkCount?: StatValue | number
  gkCountP90?: StatValue | number
  gkSuccessPct?: StatValue | number
  gkDiveCount?: StatValue | number
  gkDiveReturnCount?: StatValue | number
  gkExplosiveImpulse_Ns?: StatValue | number
  gkImpactImpulse_Ns?: StatValue | number

  // Kick count (technical load)
  kicks?: StatValue | number
  kicksPerMinute?: StatValue | number

  // Involvement workload
  involvementWorkload_J?: StatValue | number
  involvementWorkRate_Js?: StatValue | number
  offTheBallRuns?: StatValue | number
  offTheBallRunsPerMin?: StatValue | number
  offTheBallWorkload_J?: StatValue | number
  offTheBallWorkRate_Js?: StatValue | number

  // Duel workload
  duelRuns?: StatValue | number
  duelWorkload_J?: StatValue | number
  duelWorkRate_Js?: StatValue | number

  // Defensive workload
  defensiveActionRuns?: StatValue | number
  defensiveActionWorkload_J?: StatValue | number
  defensiveActionWorkRate_Js?: StatValue | number
  pressureCount?: StatValue | number
  pressureRunsPerMin?: StatValue | number
  pressureRunWorkload_J?: StatValue | number
  pressureRunWorkRate_Js?: StatValue | number

  // Take-ons
  takeOnCount?: StatValue | number
  takeOnSuccessPct?: StatValue | number

  // Shot distance
  shotDistanceFromGoalAvg_m?: StatValue | number

  // Cross stats
  crossCount?: StatValue | number
  crossCountP90?: StatValue | number
  crossSpeedAvg_mps?: StatValue | number
  crossDistAvg_m?: StatValue | number

  // Reception stats
  receptionCount?: StatValue | number
  receptionCountP90?: StatValue | number
}

// Player summary stats for American Football. Field names match the
// reporting-api payload — every metric that carries a unit uses the same
// suffix convention as soccer (_m, _mps, _kJ, _s) and percentages end in
// `Pct`. The old short-form names (totalDistance, maxSpeed, totalWork,
// completePercentage, avgReleaseVelocity, qbAvgTimeToThrow,
// qbAvgAirYardsGained) were only ever set in the FE config — the backend
// always emitted the canonical ones — so renaming here is just catching
// up to the API payload.
// See services/reporting-api/internal/api_models/team_summaries.go.
export interface AmericanFootballPlayerSummaryStats {
  // General
  totalDuration_s?: StatValue | number
  technicalLoad?: StatValue | number
  technicalLoadPerMin?: StatValue | number
  successPercentage?: StatValue | number
  playerLoad?: StatValue | number
  playerLoadPerMin?: StatValue | number
  totalDistance_m?: StatValue | number
  fieldYardsGained?: StatValue | number

  // Physical
  runningDistance?: StatValue | number
  highSpeedRunningDistance?: StatValue | number
  sprintRunningDistance?: StatValue | number
  sprintCount?: StatValue | number
  highAccelCount?: StatValue | number
  highDecelCount?: StatValue | number
  maxSpeed_mps?: StatValue | number
  avgMaxSpeed_mps?: StatValue | number
  totalMechanicalWork_kJ?: StatValue | number

  // QB Pass
  throwCount?: StatValue | number
  highVelocityThrowCount?: StatValue | number
  passCompletionPct?: StatValue | number
  initialReleaseVelocityAvg_mps?: StatValue | number
  qbAvgSpin?: StatValue | number
  qbAvgSpiral?: StatValue | number
  qbAvgHangTime?: StatValue | number
  qbAvgDistance?: StatValue | number
  airYardsGainedAvg_m?: StatValue | number
  timeToThrowAvg_s?: StatValue | number

  // WR Route
  routeCount?: StatValue | number
  routePlusPassedToCount?: StatValue | number
  catchCompletePercentage?: StatValue | number
  avgRouteDistance?: StatValue | number
  avgWorkRate?: StatValue | number

  // Kick
  kickCount?: StatValue | number
  goodKickPercentage?: StatValue | number
  kickAvgReleaseVelocity?: StatValue | number
  kickAvgSpin?: StatValue | number
  kickAvgSpiral?: StatValue | number
  kickAvgHangTime?: StatValue | number
  kickAvgDistance?: StatValue | number
  avgFieldGoalDistance?: StatValue | number
  kickAvgExecutionTime?: StatValue | number

  // Punt
  puntCount?: StatValue | number
  puntAvgReleaseVelocity?: StatValue | number
  puntAvgSpin?: StatValue | number
  puntAvgSpiral?: StatValue | number
  puntAvgHangTime?: StatValue | number
  puntAvgDistance?: StatValue | number
  avgPuntDistance?: StatValue | number
  puntAvgExecutionTime?: StatValue | number

  // Technical Load (throw bands)
  throwCountTotal?: StatValue | number
  throwCountPerMin?: StatValue | number
  throwCountDistanceBelow15?: StatValue | number
  throwCountDistanceBetween16To30?: StatValue | number
  throwCountDistanceBetween31To45?: StatValue | number
  throwCountDistanceAbove46?: StatValue | number
  throwCountReleaseVelocityLow?: StatValue | number
  throwCountReleaseVelocityMid?: StatValue | number
  throwCountReleaseVelocityHigh?: StatValue | number
}

// Union type for any sport's player summary stats.
//
// DESIGN DECISION: This union intentionally prevents direct field access like
// `stats.passesAttempted` because TypeScript can't know which sport's interface
// applies. This forces all field access through the string accessor pattern
// (casting to Record<string, unknown> and indexing by config-driven accessor key),
// which guarantees that only metrics declared in the sport config are displayed.
// See sport-config.ts and stat-utils.ts for the access pattern.
export type PlayerSummaryStats =
  | SoccerPlayerSummaryStats
  | AmericanFootballPlayerSummaryStats

// Period stats within player summary
export interface PlayerSummaryInTimePeriod {
  periodNumber?: number
  overtime?: boolean
  drillId?: string
  periodDurationSeconds?: number
  summary: PlayerSummaryStats
}

/** @deprecated Use PlayerSummaryInTimePeriod */
export type SoccerPlayerSummaryInTimePeriod = PlayerSummaryInTimePeriod

// GET /teams/{teamId}/sessions/{sessionId}/players/{playerId}/summary response
export interface PlayerSessionSummaryResponse {
  totals: PlayerSummaryStats
  timePeriodStats: PlayerSummaryInTimePeriod[]
}

// Player stats with ID for team summaries
export interface PlayerStatsWithId {
  playerId: string
  stats: PlayerSummaryStats | null
  /**
   * Backend `PlayerMetricSummary` json names for metrics this player has data
   * for in the requested period/position slice (a recorded `0` is present;
   * null/absent is not). Optional: absent when the backend has not shipped the
   * presence signal yet, in which case consumers fall back to "show all".
   * `null` (a real JSON-boundary value, not just `undefined`) behaves as absent.
   */
  metricsPresent?: string[] | null
  /** Independent of `metricsPresent` — a separate pipeline, so a player can
   *  have events with no aggregated metrics. Absent (older BE) falls back
   *  to "show all", same as `metricsPresent`. */
  hasEventData?: boolean | null
}

// GET /teams/{teamId}/sessions/{sessionId}/players/summary response
export interface AllPlayersSessionSummaryResponse {
  playerStats: PlayerStatsWithId[]
  totalStats: PlayerSummaryStats
}

// Top performance entry
export interface TopPerformanceEntry {
  playerValue: number
  player: ReportingPlayer
  teamValue?: number
}

// Team summary top performances (entries can be null when no data)
export interface TeamSummaryTopPerformances {
  totalDistance_m?: TopPerformanceEntry | null
  avgMaxSpeed_mps?: TopPerformanceEntry | null
  highIntensityAccelCount?: TopPerformanceEntry | null
}

// Soccer team-summary top lists. The dashboard formatter branches on
// `sport` and reads either this or `AmericanFootballTeamSummaryTopLists`.
export interface TeamSummaryTopLists {
  shotsAttempted: TopPerformanceEntry[]
  passesMade: TopPerformanceEntry[]
  carries: TopPerformanceEntry[]
  involvements: TopPerformanceEntry[]
}

// Per-metric top-list entry for American / Canadian Football. The
// backend ships this shape (see
// services/reporting-api/internal/api_models/team_summaries.go): each
// metric carries its own team total plus the ranked player list so the
// dashboard can render both without a second lookup.
export interface AmericanFootballTopListSection {
  teamValue: number | null
  topPlayers: TopPerformanceEntry[]
}

// American / Canadian Football top lists.
export interface AmericanFootballTeamSummaryTopLists {
  totalDistance_m: AmericanFootballTopListSection
  avgMaxSpeed_mps: AmericanFootballTopListSection
  sprintCount: AmericanFootballTopListSection
  initialReleaseVelocityAvg_mps: AmericanFootballTopListSection
  passCompletionPct: AmericanFootballTopListSection
  timeToThrowAvg_s: AmericanFootballTopListSection
}

// Individual metric with value and comparisons
export interface SummaryMetricValue {
  value: number | null
  seasonAvg: number
  maxValue: number
  minValue: number
}

// Soccer team session summary metrics (nested structure from API). Mirrors the
// backend `SoccerTeamSessionSummaryMetrics`; AMF/CFL use the sibling
// `AmericanFootballTeamSessionSummaryMetrics` below.
export interface TeamSessionSummaryMetrics {
  totalDistance_m?: SummaryMetricValue
  avgMaxSpeed_mps?: SummaryMetricValue
  highSpeedRunCount?: SummaryMetricValue
  highSpeedRunDistance_m?: SummaryMetricValue
  highMetabolicPowerDistance_m?: SummaryMetricValue
  passesMade?: SummaryMetricValue
  passesAttempted?: SummaryMetricValue
  forwardPasses?: SummaryMetricValue
  shotsAttempted?: SummaryMetricValue
  shotsOnTargetPct?: SummaryMetricValue
  conversionPct?: SummaryMetricValue
  highIntensityAccelCount?: SummaryMetricValue
  highIntensityDecelCount?: SummaryMetricValue
  involvements?: SummaryMetricValue
  involvementsP90?: SummaryMetricValue
  carries?: SummaryMetricValue
}

// AMF / CFL team session summary metrics. Mirrors the backend
// `AmericanFootballTeamSessionSummaryMetrics` — a distinct key set from soccer
// (throw velocity bands, pass completion, release velocity, time-to-throw).
export interface AmericanFootballTeamSessionSummaryMetrics {
  totalDistance_m?: SummaryMetricValue
  distancePerMinute_m?: SummaryMetricValue
  highSpeedRunDistance_m?: SummaryMetricValue
  sprintCount?: SummaryMetricValue
  avgMaxSpeed_mps?: SummaryMetricValue
  highIntensityAccelCount?: SummaryMetricValue
  highIntensityDecelCount?: SummaryMetricValue
  passesMade?: SummaryMetricValue
  passesAttempted?: SummaryMetricValue
  passCompletionPct?: SummaryMetricValue
  timeToThrowAvg_s?: SummaryMetricValue
  initialReleaseVelocityAvg_mps?: SummaryMetricValue
  throwCountLt18ms?: SummaryMetricValue
  throwCount18msTo22ms?: SummaryMetricValue
  throwCountGt22ms?: SummaryMetricValue
}

// Every valid dashboard-chart metric key, across sports. Deriving this from the
// response interfaces (rather than `string`) lets the sport configs be
// type-checked: a chart keyed on a field no summary emits is a compile error.
export type ReportingSummaryMetricKey =
  | keyof TeamSessionSummaryMetrics
  | keyof AmericanFootballTeamSessionSummaryMetrics

// GET /teams/{teamId}/sessions/{sessionId}/summary response.
// The backend emits one of `topLists` (soccer) or
// `americanFootballTopLists` (AF / CFL) depending on the team's sport —
// both fields are optional so either payload shape typechecks. The
// dashboard formatter branches on the active sport to pick the right
// source.
export interface TeamSessionSummaryResponse {
  summaryMetrics:
    | TeamSessionSummaryMetrics
    | AmericanFootballTeamSessionSummaryMetrics
  topLists?: TeamSummaryTopLists
  americanFootballTopLists?: AmericanFootballTeamSummaryTopLists
  topPerformances: TeamSummaryTopPerformances
}

// --- Action types ---

export type ActionType =
  | 'SoccerShotAtGoal'
  | 'SoccerPass'
  | 'SoccerCross'
  | 'SoccerDribble'
  | 'SoccerKickoff'
  | 'SoccerThrowIn'
  | 'SoccerCorner'
  | 'SoccerPenalty'
  | 'SoccerClearance'
  | 'AmericanFootballPass'
  | 'FootballRoute'
  | 'FootballKick'
  | 'FootballPunt'

export type ActionOutcome =
  | 'Goal'
  | 'Behind'
  | 'Missed'
  | 'BlockedOrSaved'
  | 'MissedWide'
  | 'MissedOverCrossbar'
  | 'HitPost'
  | 'OutOfBounds'
  | 'Complete'
  | 'Incomplete'
  | 'Interception'
  | 'Returned'
  | 'Rouge'
  | 'RecoveredReceivingTeam'
  | 'RecoveredKickingTeam'
  | 'OnsideKickRecovered'
  | 'FumbleRecovery'
  | 'FumbleDefensiveRecovery'
  | 'Touchdown'
  | 'Retained'

export type ActionDirection =
  | 'Unknown'
  | 'Forward'
  | 'Backward'
  | 'Left'
  | 'Middle'
  | 'Right'
  | 'Sideways'

export interface PlayerAction {
  id: string
  type: ActionType | string
  x: number | null
  y: number | null
  endX?: number | null
  endY?: number | null
  time?: string
  endTime?: string
  periodNumber?: number
  periodId?: string
  drillId?: string
  overtime?: boolean
  outcomes?: ActionOutcome[] | null
  distanceMeters?: number | null
  speedMps?: number | null
  direction?: ActionDirection | null
  passingPlayerId?: string | null
  receivingPlayerId?: string | null
  shooterPlayerId?: string | null
  playerId?: string | null
  timeOnBall?: number | null
  // Football-specific
  initialVelocity_mps?: number | null
  spin_rps?: number | null
  spiral?: number | null
  hangTime_s?: number | null
  timeToThrow_s?: number | null
  territorialGain_m?: number | null
  // Legacy compatibility
  timestamp?: number
  success?: boolean
  metadata?: Record<string, unknown>
}

// GET /teams/{teamId}/sessions/{sessionId}/players/{playerId}/actions response
export interface PlayerActionsResponse {
  actions: PlayerAction[]
}

// --- Play visualization types ---

export type PlayCategory =
  | 'Pass'
  | 'Cross'
  | 'Shot'
  | 'Carry'
  | 'Reception'
  | 'QBPass'
  // AMF/CFL outcome-keyed QB-throw categories. These replace the legacy
  // single `QBPass` pill on the football pitch view — each throw falls into
  // exactly one bucket based on its outcome (Touchdown > Interception >
  // Incomplete > Complete priority).
  | 'Complete'
  | 'Incomplete'
  | 'Interception'
  | 'Touchdown'

export interface PlayMetric {
  title: string
  value: string
}

export interface MetricOption {
  name: string
  key: string
}

// --- Performance monitoring types ---

export type PerformanceLevel = 'green' | 'orange' | 'red'

export interface PerformanceBarData {
  performance: number // 0-100 normalised
  referencePerformance?: number // 0-100 reference marker position
  level: PerformanceLevel
}

export interface StatGauge {
  title: string
  value: string
  seasonAverage: string
  actualValue: string
  performance: PerformanceBarData
  unit?: string
}

export interface PerformanceValue {
  rawValue: number
  displayValue: string
}

export interface DailyPerformance {
  daily: PerformanceValue
  avg7Days: PerformanceValue
  avg28Days: PerformanceValue
  date: Date
}
